<template>
  <div class="progress-tab">
    <v-alert v-if="!members.length" type="info" text class="mb-0">
      O progresso só pode ser consultado com alunos ativos na turma.
    </v-alert>

    <template v-else>
      <v-select
        v-model="selectedStudentId"
        :items="studentOptions"
        item-text="text"
        item-value="value"
        label="Aluno"
        outlined
        dense
        hide-details="auto"
        class="progress-tab__select"
        @change="loadProgress"
      />

      <div v-if="loading" class="progress-tab__hint mt-5">
        Carregando progresso...
      </div>

      <template v-else>
        <div class="progress-tab__summary mt-5">
          <div class="progress-tab__count">
            Concluídos: {{ progress.completedCount }} /
            {{ progress.totalLessons }}
          </div>
          <v-chip small dark :color="theme.primary">
            {{ formatPercentage(progress.completedPercentage) }}
          </v-chip>
        </div>

        <v-progress-linear
          :value="progress.completedPercentage"
          rounded
          height="8"
          class="mt-3"
          color="#16a34a"
          background-color="rgba(148, 163, 184, 0.25)"
        />

        <div class="progress-tab__hint mt-3">
          Marque os assuntos já vistos por este aluno. Útil para reposições ou
          ajustes fora do calendário.
        </div>

        <div v-if="checklist.length" class="progress-tab__list mt-4">
          <label
            v-for="subject in checklist"
            :key="subject.id"
            class="progress-item"
            :class="{ 'progress-item--done': subject.completed }"
          >
            <v-checkbox
              :input-value="subject.completed"
              :loading="togglingId === subject.id"
              :disabled="togglingId === subject.id"
              color="#16a34a"
              hide-details
              dense
              class="ma-0 pa-0 progress-item__check"
              @change="toggleSubject(subject)"
            />
            <span class="progress-item__title">{{ subject.title }}</span>
          </label>
        </div>

        <v-alert v-else type="info" text class="mt-4 mb-0">
          Nenhum assunto disponível para este aluno.
        </v-alert>
      </template>
    </template>
  </div>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";

const emptyProgress = () => ({
  totalLessons: 0,
  completedCount: 0,
  remainingCount: 0,
  completedPercentage: 0,
  completedClasses: [],
  remainingClasses: [],
  classes: [],
});

export default {
  name: "ClassProgressTab",

  props: {
    groupId: {
      type: [String, Number],
      required: true,
    },
    members: {
      type: Array,
      default: () => [],
    },
    theme: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      togglingId: null,
      selectedStudentId: null,
      progress: emptyProgress(),
    };
  },

  computed: {
    studentOptions() {
      return this.members.map((member) => ({
        text: member.name || "Aluno(a)",
        value: member.id,
      }));
    },
    checklist() {
      if (
        Array.isArray(this.progress.classes) &&
        this.progress.classes.length
      ) {
        return this.progress.classes;
      }

      // fallback: monta a partir das listas separadas (retrocompatível)
      const completed = (this.progress.completedClasses || []).map((item) => ({
        ...item,
        completed: true,
      }));
      const remaining = (this.progress.remainingClasses || []).map((item) => ({
        ...item,
        completed: false,
      }));

      return [...completed, ...remaining];
    },
  },

  watch: {
    members: {
      immediate: true,
      handler(members) {
        const exists = (members || []).some(
          (member) => member.id === this.selectedStudentId
        );

        if (!exists) {
          this.selectedStudentId =
            members && members.length ? members[0].id : null;
        }

        if (this.selectedStudentId) {
          this.loadProgress();
        }
      },
    },
  },

  methods: {
    async loadProgress() {
      if (!this.selectedStudentId) {
        this.progress = emptyProgress();
        return;
      }

      const requestedId = this.selectedStudentId;
      this.loading = true;

      try {
        const res = await partnerApi.getStudentSubjectSequence(
          this.groupId,
          requestedId
        );

        if (this.selectedStudentId !== requestedId) {
          return;
        }

        this.applyProgress(res.data);
      } catch (error) {
        this.progress = emptyProgress();
      } finally {
        this.loading = false;
      }
    },
    applyProgress(data) {
      const payload = data || {};
      this.progress = {
        totalLessons: payload.totalLessons || 0,
        completedCount: payload.completedCount || 0,
        remainingCount: payload.remainingCount || 0,
        completedPercentage: payload.completedPercentage || 0,
        completedClasses: Array.isArray(payload.completedClasses)
          ? payload.completedClasses
          : [],
        remainingClasses: Array.isArray(payload.remainingClasses)
          ? payload.remainingClasses
          : [],
        classes: Array.isArray(payload.classes) ? payload.classes : [],
      };
    },
    async toggleSubject(subject) {
      if (!this.selectedStudentId || this.togglingId) {
        return;
      }

      const nextCompleted = !subject.completed;
      this.togglingId = subject.id;

      try {
        const res = await partnerApi.setStudentSubjectProgress(
          this.groupId,
          this.selectedStudentId,
          { courseClassId: subject.id, completed: nextCompleted }
        );

        this.applyProgress(res.data);

        this.$root.$emit("display-alert", {
          text: nextCompleted
            ? "Assunto marcado como concluído."
            : "Assunto desmarcado.",
          type: 0,
        });
      } catch (error) {
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message ||
            "Nao foi possivel atualizar o progresso do aluno.",
          type: 1,
        });
      } finally {
        this.togglingId = null;
      }
    },
    formatPercentage(value) {
      return `${Number(value || 0).toFixed(1)}%`;
    },
  },
};
</script>

<style scoped>
.progress-tab__select {
  max-width: 360px;
}

.progress-tab__summary {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-tab__count {
  font-weight: 700;
  color: #1f2937;
}

.progress-tab__hint {
  color: rgba(31, 41, 55, 0.72);
  font-size: 0.92rem;
}

.progress-tab__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.progress-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
}

.progress-item--done {
  background: rgba(22, 163, 74, 0.06);
  border-color: rgba(22, 163, 74, 0.25);
}

.progress-item__check {
  flex: 0 0 auto;
}

.progress-item__title {
  font-weight: 600;
  color: #1f2937;
}

.progress-item--done .progress-item__title {
  color: #166534;
}
</style>
